/**
 * 什么时候该在消息流里要求绑定项目（设计稿「新合同 §0」）。
 *
 * # 为什么是规则不是模型
 *
 * M0–M4 是零模型调用区（CLAUDE.md §2）。这里也不该用模型：
 * 判「这句话要不要绑项目」如果交给模型，它会在同一句话上
 * **今天判要、明天判不要**，而用户看到的是一个时灵时不灵的拦截。
 * 规则会漏判，但它漏得稳定，而且漏了的后果是「没提示」，
 * 不是「莫名其妙被拦」。
 *
 * # 为什么不弹 Modal
 *
 * 冻结合同写死了：**在消息流内要求绑定，不弹阻塞 Modal。**
 * 一个 Modal 会把「我只是想问一句」变成「我必须先做一个决定」，
 * 而通用会话存在的理由正是「不用先选项目」。
 */

/** 三类会触发绑定的事。**穷举**，不是「看着像就算」。 */
export const BIND_KINDS = Object.freeze(['code', 'deploy', 'resource'] as const)

export type BindKind = (typeof BIND_KINDS)[number]

export const BIND_KIND_COPY: Readonly<Record<BindKind, string>> = Object.freeze({
  code: 'lulu_workbench.bind.kind_code',
  deploy: 'lulu_workbench.bind.kind_deploy',
  resource: 'lulu_workbench.bind.kind_resource',
})

/**
 * 每一类的触发词。
 *
 * 词表短而具体，**不做模糊匹配**——设计稿在项目搜索那边已经写过同一条
 * 「不模糊匹配硬凑几个不相关的塞给你」。这里同理：宁可漏，不可乱拦。
 */
const TRIGGERS: Readonly<Record<BindKind, readonly string[]>> = Object.freeze({
  code: ['代码', '提交', '仓库', '分支', '改一下', 'commit', 'pull request', 'PR'],
  deploy: ['部署', '发布', '上线', '回滚', 'deploy', 'rollback'],
  resource: ['服务器', '机器', '数据库', '域名', '资源', '扩容', 'SSH'],
})

/**
 * 这句话要不要绑项目。
 *
 * 已经绑了的会话直接回 `null`——**绑过就不再问**，而且冻结合同写着
 * 「绑定后不可换项目」，所以再问一次除了让人以为能换，没有别的作用。
 */
export function bindRequirement(input: {
  readonly text: string
  readonly projectId: string | null
  /**
   * Lulu 上一句是不是「这件事得挂在一个项目上」。
   *
   * # 为什么光看输入框不够
   *
   * 触发词判的是**他正在打的那句话**，于是卡片只在打字期间出现，
   * 一按发送就没了。而真正需要它的那一刻恰恰在**发送之后**——
   * 他收到「得挂在项目上」，这时输入框是空的，卡片刚好不在。
   *
   * 2026-08-29 线上就是这么走的：他连着三次说「改一下结算页的
   * 优惠券位置」（「改一下」是命中的触发词），三次都被同一句话挡回去，
   * 而**就地绑定这条路一直是通的**，只是那一刻屏幕上没有入口。
   *
   * 所以这一档不看输入框：Lulu 已经明说要项目了，那就一直摆着，
   * 直到他绑上或者点「先不绑」。
   */
  readonly replyAskedForProject?: boolean
}): BindKind | null {
  if (input.projectId !== null) return null
  const lower = input.text.toLowerCase()
  const fromDraft =
    BIND_KINDS.find((kind) =>
      TRIGGERS[kind].some((word) => lower.includes(word.toLowerCase())),
    ) ?? null
  if (fromDraft !== null) return fromDraft
  // **归到 code 这一类。** 这条 ask 只从「他提了一件要动手的活」那一句
  // 来（steward.smalltalk.work_needs_project），而那一句的前提就是要动代码。
  // 猜一个更泛的类别只会让卡片上那两个字对不上他刚说的事。
  return input.replyAskedForProject === true ? 'code' : null
}

/**
 * 他最后说的那句话是不是在提一件要动手的活。
 *
 * # 为什么判他说的，而不是判 Lulu 回的
 *
 * 第一版判的是「Lulu 最后一句恰好是那条模板」——**逐字比对**。
 * 那在 Lulu 只会挑模板的时候成立，而 arron 2026-08-29 拍板
 * 「Lulu 可以自己写句子」之后**当场失效**：它写出来的句子不会逐字
 * 等于任何模板，于是绑定卡又消失了——正是同一天早些时候刚修好的那个洞。
 *
 * 判他说的那句就没有这个问题：**触发词是我们自己的规则，
 * 不随 Lulu 的措辞变**。而这条卡片本来就是为「他提了要动手的活」出的，
 * 判据落在他那句话上比落在回话上更贴。
 *
 * 只看**最后一条人说的**：他后来又聊了别的，说明那件事已经过去了。
 */
export function humanAskedForWork(
  messages: readonly { readonly body: string; readonly isHuman: boolean }[],
): boolean {
  const lastHuman = [...messages].reverse().find((m) => m.isHuman)
  if (lastHuman === undefined) return false
  const lower = lastHuman.body.toLowerCase()
  return BIND_KINDS.some((kind) =>
    TRIGGERS[kind].some((word) => lower.includes(word.toLowerCase())),
  )
}

/**
 * Lulu 最后一句是不是在要项目。
 *
 * **比对渲染后的整句，不做子串匹配**：子串在「多了点什么」那个方向上
 * 是瞎的，而这里要判的正是「是不是恰好那一句」。两侧的文案来自同一份
 * 生成物（packages/copy），所以逐字相等是可靠的——模板改了两边一起改。
 *
 * 只看**最后一条**：他后来又聊了别的，说明那件事已经过去了。
 */
export function replyAskedForProject(
  messages: readonly { readonly body: string }[],
  workNeedsProjectText: string,
): boolean {
  const last = messages[messages.length - 1]
  return last !== undefined && last.body === workNeedsProjectText
}

/**
 * 绑定卡会不会挡住输入。
 *
 * **恒为 false，且没有入参。** 与 shouldRollbackProject 同一个写法：
 * 收了参数，就有人会传一个「这次比较重要」，而那一次就变成了 Modal。
 */
export function bindCardBlocksInput(): false {
  return false
}
